import type { FeedEngine } from '../connector/feed-engine/feed-engine';

/** Named folders of the engine, sorted like the sidebar's "move to folder" menu. */
export const getNamedFolders = (feedEngine: FeedEngine): string[] =>
  Object.keys(feedEngine.getFolders())
    .filter(name => name)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

/**
 * Moves `feedIds` into `folder` once `subscription` settled. Only feeds that actually ended up
 * followed are moved, so a failed or cancelled follow never leaves a stray id in the folders.
 */
export async function moveAfterSubscribe(feedEngine: FeedEngine, feedIds: string[], subscription: Promise<unknown>, folder: string): Promise<void> {
  await subscription.catch(() => undefined);
  const followed = new Set(feedEngine.getFeeds().map(f => f.id));
  const ids = feedIds.filter(id => followed.has(id));
  if (ids.length) await feedEngine.moveFeeds(ids, folder);
}

/** Server-side limit for folder names (FieldLength in rest-schema.conversion.ts). */
export const MAX_FOLDER_NAME_LENGTH = 128;

/** Trimmed, single-line folder name for "New folder", or undefined if unusable. */
export const cleanFolderName = (name: string): string | undefined =>
  name.replace(/\s+/g, ' ').trim().slice(0, MAX_FOLDER_NAME_LENGTH) || undefined;

const POPOVER_GAP = 6;
const VIEWPORT_MARGIN = 8;

/**
 * Viewport position for a popover under `anchor`, right-aligned with it (Follow buttons sit at
 * the right of their cards). Flips above the anchor when it doesn't fit below but does above,
 * and is kept horizontally inside the viewport.
 */
export function placePopover(anchor: { top: number; bottom: number; right: number }, size: { width: number; height: number },
                             viewport: { width: number; height: number }): { left: number; top: number } {
  const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.right - size.width, viewport.width - size.width - VIEWPORT_MARGIN));
  const below = anchor.bottom + POPOVER_GAP;
  const above = anchor.top - POPOVER_GAP - size.height;
  const fitsBelow = below + size.height <= viewport.height - VIEWPORT_MARGIN;
  return { left, top: !fitsBelow && above >= VIEWPORT_MARGIN ? above : below };
}
